import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import { useGetOverviewDashboardQuery } from "slices/addOnApi";

import GroupIcon from "@mui/icons-material/Group";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SpaIcon from "@mui/icons-material/Spa";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LoginIcon from "@mui/icons-material/Login";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArticleIcon from "@mui/icons-material/Article";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

ChartJS.register(
  LineElement,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
);

const ROLE_COLORS = {
  user: "#6C63FF",
  beautician: "#FF6584",
  doctor: "#43C6AC",
  clinic: "#F7971E",
  guest: "#a0aec0",
};

const StatCard = ({ icon, label, value, sub }) => (
  <Card
    sx={{
      p: 2.5,
      borderRadius: 3,
      height: "100%",
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          textTransform="uppercase"
          letterSpacing={1}
        >
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={700} mt={0.5}>
          {value ?? 0}
        </Typography>
        {sub && (
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <TrendingUpIcon sx={{ fontSize: 14, color: "#22c55e" }} />
            <Typography variant="caption" color="#22c55e" fontWeight={600}>
              {sub}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          background: "rgba(68,33,65,0.1)",
          borderRadius: 2,
          p: 1.2,
          display: "flex",
        }}
      >
        {icon}
      </Box>
    </Box>
  </Card>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="h6"
    fontWeight={700}
    mb={2}
    sx={{ color: "#442141", borderLeft: "4px solid #442141", pl: 1.5 }}
  >
    {children}
  </Typography>
);

const statusColor = {
  completed: "success",
  delivered: "success",
  paid: "success",
  active: "success",
  "in-progress": "warning",
  processing: "warning",
  pending: "warning",
  cancelled: "error",
  inactive: "error",
  failed: "error",
  "main-admin": "info",
  staff: "default",
};
const statusChip = (s) => (
  <Chip
    label={s}
    size="small"
    color={statusColor[s] || "default"}
    sx={{ fontWeight: 600, fontSize: 11 }}
  />
);

const tableLayoutSx = {
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "collapse",
  "& .MuiTableCell-root": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "middle",
    boxSizing: "border-box",
    minWidth: 0,
  },
};

const tableRowFlexSx = {
  display: "flex",
  width: "100%",
};

const tableCellFlexSx = (width) => ({
  flex: `0 0 ${width}`,
  width,
  maxWidth: width,
  minWidth: width,
});

const chartBase = (theme) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: { color: theme.palette.text.primary, font: { size: 11 } },
    },
    tooltip: { mode: "index", intersect: false },
  },
  scales: {
    x: {
      ticks: { color: theme.palette.text.secondary, font: { size: 10 } },
      grid: { color: theme.palette.divider },
    },
    y: {
      beginAtZero: true,
      ticks: { color: theme.palette.text.secondary, font: { size: 10 } },
      grid: { color: theme.palette.divider },
    },
  },
});

function Dashboard() {
  const theme = useTheme();
  const { data, isLoading, isError } = useGetOverviewDashboardQuery();

  if (isLoading)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress sx={{ color: "#442141" }} />
        </Box>
        <Footer />
      </DashboardLayout>
    );

  if (isError || !data)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <Typography color="error">Failed to load dashboard data.</Typography>
        </Box>
        <Footer />
      </DashboardLayout>
    );

  const {
    userCounts,
    beauticianCounts,
    orderCounts,
    userRoles,
    devices,
    logins,
    scanStats,
    monthlyScan,
    orderEarnings,
    monthlyGraph,
    adminUsers,
    beauticianAreaMap,
    recentOrders,
    recentUsers,
    blogStats,
    reportStats,
  } = data;

  const months = monthlyGraph?.map((d) => d.month) || [];

  const earningLineData = {
    labels: months,
    datasets: [
      {
        label: "Beauty Earnings (₹)",
        data: monthlyGraph?.map((d) => d.beautyEarn) || [],
        borderColor: "#FF6584",
        backgroundColor: "rgba(255,101,132,0.1)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
      },
      {
        label: "E-commerce Earnings (₹)",
        data: monthlyGraph?.map((d) => d.ecomEarn) || [],
        borderColor: "#6C63FF",
        backgroundColor: "rgba(108,99,255,0.1)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
      },
    ],
  };

  const scanLineData = {
    labels: monthlyScan?.map((d) => d.month) || [],
    datasets: [
      {
        label: "Scans",
        data: monthlyScan?.map((d) => d.scans) || [],
        borderColor: "#F7971E",
        backgroundColor: "rgba(247,151,30,0.15)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        yAxisID: "y",
      },
      {
        label: "Scan Earn (₹)",
        data: monthlyScan?.map((d) => d.earnings) || [],
        borderColor: "#6C63FF",
        backgroundColor: "rgba(108,99,255,0.1)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        yAxisID: "y1",
      },
    ],
  };

  const scanOpts = {
    ...chartBase(theme),
    scales: {
      x: chartBase(theme).scales.x,
      y: {
        ...chartBase(theme).scales.y,
        position: "left",
        title: { display: true, text: "Scans" },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        grid: { drawOnChartArea: false },
        title: { display: true, text: "₹ Earn" },
        ticks: { color: theme.palette.text.secondary },
      },
    },
  };

  const deviceDoughnut = {
    labels: ["Android", "iOS", "Web"],
    datasets: [
      {
        data: [devices?.android || 0, devices?.ios || 0, devices?.web || 0],
        backgroundColor: ["#43C6AC", "#6C63FF", "#F7971E"],
        borderWidth: 0,
      },
    ],
  };

  const loginDoughnut = {
    labels: ["App", "Web"],
    datasets: [
      {
        data: [logins?.app || 0, logins?.web || 0],
        backgroundColor: ["#FF6584", "#6C63FF"],
        borderWidth: 0,
      },
    ],
  };

  const totalUsers = userRoles?.reduce((a, b) => a + b.count, 0) || 0;

  const userRoleBar = {
    labels: userRoles?.map((r) => r.role) || [],
    datasets: [
      {
        label: "Users by Role",
        data: userRoles?.map((r) => r.count) || [],
        backgroundColor:
          userRoles?.map((r) => ROLE_COLORS[r.role] || "#888") || [],
        borderRadius: 6,
      },
    ],
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* KPI CARDS */}
        <Grid container spacing={2.5} mb={4}>
          {[
            {
              icon: <GroupIcon sx={{ color: "#442141", fontSize: 28 }} />,
              label: "Total Users",
              value: userCounts?.totalUserCount?.toLocaleString(),
              sub: `+${userCounts?.todayUserCount || 0} today`,
            },
            {
              icon: <SpaIcon sx={{ color: "#442141", fontSize: 28 }} />,
              label: "Beauticians",
              value: beauticianCounts?.totalBeauticianCount?.toLocaleString(),
              sub: `+${beauticianCounts?.todayBeauticianCount || 0} today`,
            },
            {
              icon: (
                <ShoppingCartIcon sx={{ color: "#442141", fontSize: 28 }} />
              ),
              label: "Total Orders",
              value: orderCounts?.totalOrderCount?.toLocaleString(),
              sub: `+${orderCounts?.todayOrderCount || 0} today`,
            },
            {
              icon: (
                <CurrencyRupeeIcon sx={{ color: "#442141", fontSize: 28 }} />
              ),
              label: "Beauty Earn Today",
              value: `₹${(orderEarnings?.beauty?.today || 0).toLocaleString()}`,
              sub: `Week ₹${(
                orderEarnings?.beauty?.week || 0
              ).toLocaleString()}`,
            },
            {
              icon: (
                <CurrencyRupeeIcon sx={{ color: "#442141", fontSize: 28 }} />
              ),
              label: "Ecom Earn Today",
              value: `₹${(
                orderEarnings?.ecommerce?.today || 0
              ).toLocaleString()}`,
              sub: `Week ₹${(
                orderEarnings?.ecommerce?.week || 0
              ).toLocaleString()}`,
            },
            {
              icon: (
                <FaceRetouchingNaturalIcon
                  sx={{ color: "#442141", fontSize: 28 }}
                />
              ),
              label: "Total Scans",
              value: scanStats?.totalScans,
              sub: `+${scanStats?.todayScans || 0} today`,
            },
            {
              icon: (
                <PhoneAndroidIcon sx={{ color: "#442141", fontSize: 28 }} />
              ),
              label: "Devices Registered",
              value: devices?.total?.toLocaleString(),
              sub: "Android · iOS · Web",
            },
            {
              icon: <LoginIcon sx={{ color: "#442141", fontSize: 28 }} />,
              label: "Total Logins",
              value: logins?.total?.toLocaleString(),
              sub: "App + Web",
            },
            {
              icon: <ArticleIcon sx={{ color: "#442141", fontSize: 28 }} />,
              label: "Total Blogs",
              value: blogStats?.totalBlogs ?? 0,
              sub: `+${blogStats?.todayBlogs || 0} today`,
            },
            {
              icon: <PermMediaIcon sx={{ color: "#442141", fontSize: 28 }} />,
              label: "Media Coverage",
              value: blogStats?.totalMedia ?? 0,
              sub: `+${blogStats?.todayMedia || 0} today`,
            },
            {
              icon: <ReportProblemIcon sx={{ color: "#442141", fontSize: 28 }} />,
              label: "User Report Issues",
              value: reportStats?.userActive ?? 0,
              sub: `+${reportStats?.userToday || 0} today`,
            },
            {
              icon: <ReportProblemIcon sx={{ color: "#442141", fontSize: 28 }} />,
              label: "Beautician Report Issues",
              value: reportStats?.beauticianActive ?? 0,
              sub: `+${reportStats?.beauticianToday || 0} today`,
            },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <StatCard {...c} />
            </Grid>
          ))}
        </Grid>

        {/* EARNINGS LINE CHART */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SectionTitle>Monthly Earnings — Beauty vs E-commerce</SectionTitle>
          <Box sx={{ height: 320 }}>
            <Line data={earningLineData} options={chartBase(theme)} />
          </Box>
        </Card>

        {/* SCAN PAY + DEVICE + LOGIN */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SectionTitle>Skin AI — Scans & Earnings (ScanPay)</SectionTitle>
              <Box sx={{ height: 260 }}>
                <Line data={scanLineData} options={scanOpts} />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {[
                  { label: "Total Scans", value: scanStats?.totalScans },
                  {
                    label: "Total Earn",
                    value: `₹${(
                      scanStats?.totalEarnings || 0
                    ).toLocaleString()}`,
                  },
                  {
                    label: "Avg/Scan",
                    value:
                      scanStats?.totalScans > 0
                        ? `₹${Math.round(
                            scanStats.totalEarnings / scanStats.totalScans,
                          )}`
                        : "₹0",
                  },
                  {
                    label: "Success Rate",
                    value: `${scanStats?.successRate || 0}%`,
                  },
                ].map((s, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <Box
                      sx={{
                        background: "#f7e8d9",
                        borderRadius: 2,
                        p: 1.5,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {s.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="#442141">
                        {s.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SectionTitle>Devices</SectionTitle>
              <Box sx={{ height: 180 }}>
                <Doughnut
                  data={deviceDoughnut}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { font: { size: 11 } },
                      },
                    },
                  }}
                />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[
                ["Android", devices?.android],
                ["iOS", devices?.ios],
                ["Web", devices?.web],
              ].map(([k, v]) => (
                <Box
                  key={k}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {k}
                  </Typography>
                  <Chip
                    label={v || 0}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                </Box>
              ))}
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SectionTitle>Login History</SectionTitle>
              <Box sx={{ height: 180 }}>
                <Doughnut
                  data={loginDoughnut}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { font: { size: 11 } },
                      },
                    },
                  }}
                />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[
                ["App", logins?.app],
                ["Web", logins?.web],
              ].map(([k, v]) => (
                <Box
                  key={k}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {k}
                  </Typography>
                  <Chip
                    label={(v || 0).toLocaleString()}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                </Box>
              ))}
              <Box
                mt={1.5}
                sx={{
                  background: "#f7e8d9",
                  borderRadius: 2,
                  p: 1.2,
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight={700} color="#442141">
                  {(logins?.total || 0).toLocaleString()}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* USER ROLES BAR + EARNING SUMMARY */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SectionTitle>Users by Role</SectionTitle>
              <Box sx={{ height: 220 }}>
                <Bar
                  data={userRoleBar}
                  options={{
                    ...chartBase(theme),
                    plugins: { legend: { display: false } },
                  }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
                <Table sx={tableLayoutSx}>
                  <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "30%" }} />
                  </colgroup>

                  <TableHead sx={{ display: "block" }}>
                    <TableRow sx={{ ...tableRowFlexSx, background: "#f7e8d9" }}>
                      <TableCell
                        sx={{
                          ...tableCellFlexSx("100%"),
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        Role
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellFlexSx("100%"),
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        Count
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellFlexSx("100%"),
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        Share
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userRoles?.map((r) => (
                      <TableRow key={r.role} hover>
                        <TableCell sx={tableCellFlexSx("60%")}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                flexShrink: 0,
                                background: ROLE_COLORS[r.role] || "#888",
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ textTransform: "capitalize" }}
                            >
                              {r.role}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={tableCellFlexSx("20%")}>
                          <Typography variant="body2" fontWeight={600}>
                            {r.count.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableCellFlexSx("20%")}>
                          <Chip
                            label={`${
                              totalUsers > 0
                                ? ((r.count / totalUsers) * 100).toFixed(1)
                                : 0
                            }%`}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: 11 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                height: "100%",
              }}
            >
              <SectionTitle>Earning Summary</SectionTitle>
              {[
                {
                  label: "Beauty — Today",
                  value: `₹${(
                    orderEarnings?.beauty?.today || 0
                  ).toLocaleString()}`,
                  color: "#FF6584",
                },
                {
                  label: "Beauty — This Week",
                  value: `₹${(
                    orderEarnings?.beauty?.week || 0
                  ).toLocaleString()}`,
                  color: "#FF6584",
                },
                {
                  label: "Beauty — Total",
                  value: `₹${(
                    orderEarnings?.beauty?.total || 0
                  ).toLocaleString()}`,
                  color: "#FF6584",
                },
                {
                  label: "E-com — Today",
                  value: `₹${(
                    orderEarnings?.ecommerce?.today || 0
                  ).toLocaleString()}`,
                  color: "#6C63FF",
                },
                {
                  label: "E-com — This Week",
                  value: `₹${(
                    orderEarnings?.ecommerce?.week || 0
                  ).toLocaleString()}`,
                  color: "#6C63FF",
                },
                {
                  label: "E-com — Total",
                  value: `₹${(
                    orderEarnings?.ecommerce?.total || 0
                  ).toLocaleString()}`,
                  color: "#6C63FF",
                },
                {
                  label: "Total — Today",
                  value: `₹${(
                    orderEarnings?.total?.today || 0
                  ).toLocaleString()}`,
                  color: "#43C6AC",
                },
                {
                  label: "Total — This Week",
                  value: `₹${(
                    orderEarnings?.total?.week || 0
                  ).toLocaleString()}`,
                  color: "#43C6AC",
                },
                {
                  label: "Total — All Time",
                  value: `₹${(
                    orderEarnings?.total?.all || 0
                  ).toLocaleString()}`,
                  color: "#43C6AC",
                },
                {
                  label: "SkinAI — Today",
                  value: `₹${(scanStats?.todayEarnings || 0).toLocaleString()}`,
                  color: "#F7971E",
                },
                {
                  label: "SkinAI — This Week",
                  value: `₹${(scanStats?.weekEarnings || 0).toLocaleString()}`,
                  color: "#F7971E",
                },
                {
                  label: "SkinAI — Total",
                  value: `₹${(scanStats?.totalEarnings || 0).toLocaleString()}`,
                  color: "#F7971E",
                },
              ].map((s, i) => (
                <Box
                  key={i}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.7}
                  borderBottom="1px solid #f0f0f0"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: s.color,
                      }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {s.label}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#442141"
                  >
                    {s.value}
                  </Typography>
                </Box>
              ))}
            </Card>
          </Grid>
        </Grid>

        {/* ADMIN USERS */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AdminPanelSettingsIcon sx={{ color: "#442141" }} />
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: "#442141",
                borderLeft: "4px solid #442141",
                pl: 1.5,
              }}
            >
              Admin Users (Staff & Main Admin)
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ ...tableLayoutSx, display: "block" }}>
              <colgroup>
                <col style={{ width: "8%" }} />
                <col style={{ width: "67%" }} />
                <col style={{ width: "25%" }} />
              </colgroup>
              <TableHead sx={{ display: "block" }}>
                <TableRow sx={{ ...tableRowFlexSx, background: "#f7e8d9" }}>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("8%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("67%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("25%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Role
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ display: "block" }}>
                {adminUsers &&
                  adminUsers.length > 0 &&
                  adminUsers?.map((a, i) => (
                    <TableRow key={i} hover sx={tableRowFlexSx}>
                      <TableCell sx={tableCellFlexSx("8%")}>
                        <Typography variant="body2">{i + 1}</Typography>
                      </TableCell>
                      <TableCell sx={tableCellFlexSx("67%")}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: 13,
                              flexShrink: 0,
                              background: "#442141",
                            }}
                          >
                            {a.name?.[0]}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {a.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tableCellFlexSx("25%")}>
                        {(() => {
                          const normalizedRole = (a?.role || "").toLowerCase();
                          if (
                            normalizedRole === "main-admin" ||
                            normalizedRole === "main_admin" ||
                            normalizedRole === "mainadmin"
                          ) {
                            return (
                              <Chip
                                label="Main Admin"
                                size="small"
                                color="info"
                                sx={{ fontWeight: 600, fontSize: 11 }}
                              />
                            );
                          }

                          if (normalizedRole === "staff") {
                            return (
                              <Chip
                                label="Staff"
                                size="small"
                                color="default"
                                sx={{ fontWeight: 600, fontSize: 11 }}
                              />
                            );
                          }

                          return (
                            <Chip
                              label={a?.role || "Unknown"}
                              size="small"
                              color="default"
                              sx={{ fontWeight: 600, fontSize: 11 }}
                            />
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* BEAUTICIAN COUNT BY CITY */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <LocationOnIcon sx={{ color: "#442141" }} />
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: "#442141",
                borderLeft: "4px solid #442141",
                pl: 1.5,
              }}
            >
              Beautician Count by City
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ ...tableLayoutSx, display: "block" }}>
              <colgroup>
                <col style={{ width: "8%" }} />
                <col style={{ width: "67%" }} />
                <col style={{ width: "25%" }} />
              </colgroup>
              <TableHead sx={{ display: "block" }}>
                <TableRow sx={{ ...tableRowFlexSx, background: "#f7e8d9" }}>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("8%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("67%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    City
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("25%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Beauticians
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ display: "block" }}>
                {Object.entries(
                  (beauticianAreaMap || []).reduce((acc, b) => {
                    const city = b.cityName || "Unknown";
                    acc[city] = (acc[city] || 0) + b.beauticianCount;
                    return acc;
                  }, {}),
                )
                  .sort((a, b) => b[1] - a[1])
                  .map(([city, count], i) => (
                    <TableRow key={i} hover sx={tableRowFlexSx}>
                      <TableCell sx={tableCellFlexSx("8%")}>
                        <Typography variant="body2">{i + 1}</Typography>
                      </TableCell>
                      <TableCell sx={tableCellFlexSx("67%")}>
                        <Typography variant="body2" fontWeight={600}>
                          {city}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableCellFlexSx("25%")}>
                        <Chip
                          label={count}
                          size="small"
                          color="secondary"
                          sx={{ fontWeight: 700, fontSize: 12 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* RECENT REGISTERED USERS */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SectionTitle>Recent Registered Users (Last 10)</SectionTitle>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ ...tableLayoutSx, display: "block" }}>
              <colgroup>
                <col style={{ width: "5%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
              </colgroup>
              <TableHead sx={{ display: "block" }}>
                <TableRow sx={{ ...tableRowFlexSx, background: "#f7e8d9" }}>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("5%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("18%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("14%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Mobile
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("22%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("11%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Gender
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("11%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("11%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Joined
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ display: "block" }}>
                {recentUsers?.map((u, i) => (
                  <TableRow key={i} hover sx={tableRowFlexSx}>
                    <TableCell sx={tableCellFlexSx("5%")}>
                      <Typography variant="body2">{i + 1}</Typography>
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("18%")}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 12,
                            flexShrink: 0,
                            background: "#6C63FF",
                          }}
                        >
                          {u.name?.[0] || "U"}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {u.name || "—"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("14%")}>
                      <Typography variant="body2" noWrap>
                        {u.mobile || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("22%")}>
                      <Typography variant="body2" noWrap>
                        {u.email || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("11%")}>
                      <Chip
                        label={u.gender || "—"}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11, textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("11%")}>
                      {statusChip(u.status)}
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("11%")}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {u.created
                          ? new Date(u.created).toLocaleDateString()
                          : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* RECENT ORDERS */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SectionTitle>Recent Orders (Beauty & E-commerce)</SectionTitle>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ ...tableLayoutSx, display: "block" }}>
              <colgroup>
                <col style={{ width: "18%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "17%" }} />
              </colgroup>
              <TableHead sx={{ display: "block" }}>
                <TableRow sx={{ ...tableRowFlexSx, background: "#f7e8d9" }}>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("18%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    User
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("13%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("13%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("14%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Grand Total
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("12%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Payment
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("13%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Pay Status
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableCellFlexSx("17%"),
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Order Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ display: "block" }}>
                {recentOrders?.map((o, i) => (
                  <TableRow key={i} hover sx={tableRowFlexSx}>
                    <TableCell sx={tableCellFlexSx("18%")}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {o.user?.name || "Guest"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("13%")}>
                      <Chip
                        label={o.orderType}
                        size="small"
                        color={
                          o.orderType === "beauty" ? "secondary" : "primary"
                        }
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("13%")}>
                      <Typography variant="body2">
                        ₹{(o.totalAmount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("14%")}>
                      <Typography variant="body2" fontWeight={700}>
                        ₹{(o.grandTotal || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("12%")}>
                      <Chip
                        label={(o.paymentMethod || "").toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("13%")}>
                      {statusChip(o.paymentStatus)}
                    </TableCell>
                    <TableCell sx={tableCellFlexSx("17%")}>
                      {statusChip(o.serviceStatus || o.orderStatus)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
